// All the companies below a person or a company: the companies it takes part in,
// the companies that those companies take part in, and so on.
//
// The walk is breadth-first. It sends one query for each company (the query of
// "Participates in"), a few at the same time. A company shows one time only, under
// the first company that found it. The limits keep the load on kinedb small.

import { engagements } from './relations.js';

export const WALK_LIMITS = { maxCompanies: 500, maxQueries: 150, concurrency: 3 };

const OWNER_ROLES = new Set(['Ejer', 'Reel ejer', 'Stiftere']);

/** True when the relation is ownership: a share, a former share, or an owner role. */
export function isOwnership(node) {
  return node.share !== null || node.wasOwner || node.roles.some((role) => OWNER_ROLES.has(role));
}

/**
 * `{nodes, queries, stopped}`. A node is an engagement (relations.js) with `depth`,
 * `parent` (the enhedsnummer of the company or person above it) and `followed`: the
 * walk goes below a node only when `follow(node)` is true (default: always). `stopped` is null,
 * 'companies', 'queries' or 'aborted'. `fetchCompanies(enhedsNummer)` returns the company
 * documents in which `enhedsNummer` takes part.
 */
export async function relatedCompanies(startId, fetchCompanies, options = {}) {
  const { maxCompanies, maxQueries, concurrency } = { ...WALK_LIMITS, ...options };
  const { onProgress, signal, follow = () => true } = options;
  const start = Number(startId);
  const seen = new Set([start]);
  const nodes = [];
  let queries = 0;
  let stopped = null;
  let level = [{ enhedsNummer: start, depth: 0 }];

  while (level.length > 0 && !stopped) {
    const next = [];
    for (let i = 0; i < level.length && !stopped; i += concurrency) {
      if (signal?.aborted) { stopped = 'aborted'; break; }
      const room = maxQueries - queries;
      if (room <= 0) { stopped = 'queries'; break; }
      const chunk = level.slice(i, i + Math.min(concurrency, room));
      // The results are used in the order of the chunk, so the parent of a company
      // does not depend on which query answers first.
      const results = await Promise.all(chunk.map(async (item) => {
        const companies = await fetchCompanies(item.enhedsNummer);
        queries += 1;
        onProgress?.({ queries, companies: nodes.length });
        return companies;
      }));
      for (let j = 0; j < chunk.length && !stopped; j += 1) {
        const parent = chunk[j];
        const { active, inactive } = engagements(results[j], parent.enhedsNummer);
        for (const item of [...active, ...inactive]) {
          const id = Number(item.enhedsNummer);
          if (seen.has(id)) continue;
          if (nodes.length >= maxCompanies) { stopped = 'companies'; break; }
          seen.add(id);
          const node = { ...item, depth: parent.depth + 1, parent: parent.enhedsNummer };
          node.followed = Boolean(follow(node));
          nodes.push(node);
          if (node.followed) next.push(node);
        }
      }
    }
    level = next;
  }
  return { nodes, queries, stopped };
}

/** The nodes as a tree: `[{node, children}]` under the start. */
export function nodeTree(nodes, startId) {
  const children = new Map();
  for (const node of nodes) {
    const key = Number(node.parent);
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(node);
  }
  const build = (id) => (children.get(Number(id)) ?? []).map((node) => ({ node, children: build(node.enhedsNummer) }));
  return build(startId);
}
